import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import * as z from 'zod';
import { useNow } from '../../../../shared/hooks/use-now.js';
import { ProgressLayout } from '../../components/layouts/progress-layout.js';
import { PatchInviteForm } from '../../components/patch-invite-form.js';
import { useEnrichedInvite } from '../../hooks/use-enriched-invite.js';
import { useFormattedDateTime } from '../../hooks/use-formatted-date-time.js';

dayjs.extend(timezone);

export const schema = z.object({
  contactConsent: z.union([z.literal('yes'), z.literal('no')]),
});

function extractDefaults({ contactConsent }) {
  return { contactConsent: contactConsent === true ? 'yes' : 'no' };
}

function transformValues({ contactConsent }) {
  return {
    contactConsent: contactConsent === 'yes',
  };
}

export function AppointmentReserved(props) {
  const invite = useEnrichedInvite();
  const { t } = useTranslation('page.appointment-reserved');
  const now = useNow();

  const {
    t: expires,
    formattedDate,
    formattedTime,
  } = useFormattedDateTime(invite.appointment?.expiryDate ?? invite.expiryDate);
  const { deadlineText, deadlineTime } = useMemo(() => {
    const deadlineTime = expires.diff(now, 'hour');
    let deadlineText;
    if (expires.isSame(now, 'day')) {
      deadlineText = t('common:timeToday', { time: formattedTime });
    } else if (expires.isSame(now.add(1, 'day'), 'day')) {
      deadlineText = t('common:timeTomorrow', { time: formattedTime });
    } else {
      deadlineText = t('common:formattedDate', { date: formattedDate, time: formattedTime });
    }
    return { deadlineText, deadlineTime };
  }, [expires, formattedDate, formattedTime, now, t]);

  return (
    <PatchInviteForm
      extractDefaults={extractDefaults}
      transformValues={transformValues}
      schema={schema}
      nextPath={props.next.path}
    >
      <ProgressLayout {...props} className="bg-primary-50" headerClassName="border-b-0">
        <h1 className="title pt-1">{t('title')}</h1>

        <Trans
          i18nKey="text"
          ns="page.appointment-reserved"
          components={{
            p: <p className="mb-5" />,
            strong: <strong className="font-semibold" />,
            Status: <strong className="font-semibold text-warning-600" />,
          }}
          values={invite}
        />

        <div className="pt-2.5">
          <Trans
            i18nKey="description"
            ns="page.appointment-reserved"
            components={{
              p: <p />,
              strong: <strong className="font-semibold" />,
              b: <strong className="font-medium" />,
              ul: <ul className="pl-6 list-disc" />,
              li: <li />,
              span: <p className="mb-0" />,
            }}
            values={{ deadlineText, deadlineTime }}
          />
        </div>
      </ProgressLayout>
    </PatchInviteForm>
  );
}

/*
function ComingSoon() {
  const { t: tCommon } = useTranslation();
  const { t } = useTranslation('page.appointment-reserved');
  const { watch, setValue } = useFormContext();
  const consent = watch('contactConsent');

  const options = useMemo(
    () => [
      {
        value: 'yes',
        label: t('contactConsent.yes'),
      },
      {
        value: 'no',
        label: t('contactConsent.no'),
      },
    ],
    [t],
  );

  return (
    <div className="mb-2.5 p-3.75 bg-white rounded-lg border border-primary-300 shadow-sm">
      <Transition
        show={!consent}
        enter="transition-opacity duration-150"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-0"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <h3 className="subtitle mb-3.75">{t('comingSoonTitle')}</h3>

        <div className="mb-3.75">
          <Trans
            i18nKey="comingSoonText"
            ns="page.appointment-reserved"
            components={{
              p: <p />,
            }}
          />
        </div>

        <RadioCardList
          name="contactConsent"
          options={options}
          labelClassName="w-full !text-body"
          groupClassName="gap-y-2"
        />
      </Transition>

      {options.map(({ value }) => (
        <Transition
          show={consent === value}
          enter="transition-opacity duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-0"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          key={value}
        >
          <div className="flex items-center justify-between gap-3.75">
            <p className="mb-0">{t(`contactConsentDetails.${value}`)}</p>

            <button
              type="button"
              onClick={() => setValue('contactConsent', '')}
              className="caption font-medium text-primary hover:text-primary-hover"
            >
              {tCommon('change')}
            </button>
          </div>
        </Transition>
      ))}
    </div>
  );
}
*/
